import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import RelocationAffiliate from '../components/RelocationAffiliate';

const cityCards = [
  { name: 'Mexico City (CDMX)', tag: 'Most Popular', desc: 'The cosmopolitan capital. World-class infrastructure, 3hr from Dallas, Central Time. Best for remote workers, executives, and families.', cost: '$900-$1,400/mo', path: '/relocation/cdmx', img: 'https://images.unsplash.com/photo-1702146829082-903ae8ceca28?w=600&q=80' },
  { name: 'Mérida', tag: 'Best for Retirees', desc: 'Colonial charm, low cost of living, safe, and warm. The most livable city in Mexico for retirees and slow-living remote workers.', cost: '$500-$900/mo', path: '/relocation/merida', img: 'https://images.unsplash.com/photo-1668656985972-e57865a85efb?w=600&q=80' },
  { name: 'Tulum', tag: 'Digital Nomad Hotspot', desc: 'Jungle meets Caribbean. Booming expat scene, boutique coworking, world-class beaches.', cost: '$800-$1,500/mo', path: '/relocation/tulum', img: 'https://images.unsplash.com/photo-1746138687997-7e767a36cb88?w=600&q=80' },
  { name: 'San Miguel de Allende', tag: 'Arts & Culture', desc: 'UNESCO World Heritage city. Large American expat community, temperate climate, colonial architecture.', cost: '$700-$1,200/mo', path: '/relocation/san-miguel-de-allende', img: 'https://images.unsplash.com/photo-1717654543196-4d6826ec4bb8?w=600&q=80' },
  { name: 'Puerto Vallarta', tag: 'Beach + City Life', desc: 'Pacific coast gem. International airport, established expat infrastructure, year-round warm weather.', cost: '$700-$1,300/mo', path: '/relocation/puerto-vallarta', img: 'https://images.unsplash.com/photo-1619820074490-465bba65c13e?w=600&q=80' },
  { name: 'Playa del Carmen', tag: 'Caribbean Lifestyle', desc: 'Riviera Maya base camp. Walkable 5th Avenue, Caribbean beaches, growing remote work infrastructure.', cost: '$700-$1,200/mo', path: '/relocation/playa-del-carmen', img: 'https://images.unsplash.com/photo-1748570569916-821f08424665?w=600&q=80' },
];

const stats = [
  { value: '$2,500/mo', label: 'Avg comfortable lifestyle vs $5,500+ in major US cities' },
  { value: '3 hours', label: 'From Dallas, Houston, and Los Angeles' },
  { value: '1.5M+', label: 'Americans already living in Mexico' },
];

const RelocationHub = () => (
  <div className="min-h-screen bg-[#FAF7F2]">
    <SEO title="Relocate to Mexico: The Complete Guide for Americans (2026)" description="The most trusted relocation guide for Americans moving to Mexico. City guides, visa info, housing, banking, healthcare, and vetted resources by Mundalo." path="/relocation" />
    <Header currentPage="relocation" />

    {/* Hero with Image */}
    <section className="relative">
      <div className="h-[70vh] lg:h-screen lg:max-h-none relative overflow-hidden">
        <picture>
          {/* Mobile-first WebP */}
          <source 
            media="(max-width: 480px)"
            type="image/webp"
            srcSet="/assets/images/relocation/mexico-hero-480.webp"
          />
          <source 
            media="(max-width: 768px)"
            type="image/webp"
            srcSet="/assets/images/relocation/mexico-hero-768.webp"
          />
          <source 
            media="(max-width: 1280px)"
            type="image/webp"
            srcSet="/assets/images/relocation/mexico-hero-1280.webp"
          />
          <source 
            type="image/webp"
            srcSet="/assets/images/relocation/mexico-hero-1920.webp"
          />
          {/* JPEG fallback */}
          <img 
            src="/assets/images/relocation/mexico-hero-1920.jpg"
            srcSet="/assets/images/relocation/mexico-hero-480.jpg 480w, /assets/images/relocation/mexico-hero-768.jpg 768w, /assets/images/relocation/mexico-hero-1280.jpg 1280w, /assets/images/relocation/mexico-hero-1920.jpg 1920w"
            sizes="100vw"
            alt="Colorful colonial street in Mexico" 
            className="w-full h-full object-cover object-center block"
            data-testid="relocation-hero-image"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="1920"
            height="1080"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF]/80 via-[#FFFFFF]/40 to-transparent" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 pb-8 lg:pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[#E8A830] text-sm font-medium tracking-widest uppercase mb-5" data-testid="sub-brand">Relocation by Mundalo</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="hub-hero-title">
            Live the Life You've<br className="hidden sm:block" /> Been Planning
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-10">
            Expert relocation advisory for Americans moving to Mexico - from first question to first morning in your new home.
          </p>
          <Link to="/relocation/blueprint" className="inline-flex items-center gap-2 bg-[#E8A830] text-white px-8 py-4 text-sm font-semibold rounded-lg hover:bg-[#a8511f] transition-all" data-testid="hero-cta">
            Start Your Blueprint <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>

    {/* City Grid */}
    <section className="py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <h2 className="text-3xl lg:text-4xl text-[#FFFFFF] mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="choose-city-heading">Choose Your City</h2>
        <p className="text-[#888] text-center mb-14 max-w-xl mx-auto">Each city has its own character. Find the one that fits your lifestyle.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cityCards.map((c) => (
            <Link key={c.path} to={c.path} className="group rounded-xl overflow-hidden border border-[#e0dbd4] bg-white hover:shadow-xl transition-all" data-testid={`city-card-${c.path.split('/').pop()}`}>
              <div className="h-48 overflow-hidden"><img src={c.img} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
              <div className="p-6">
                <span className="text-[10px] font-semibold text-[#E8A830] uppercase tracking-wider">{c.tag}</span>
                <h3 className="text-lg text-[#FFFFFF] font-semibold mt-1 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{c.name}</h3>
                <p className="text-[#666] text-sm leading-relaxed mb-4">{c.desc}</p>
                <div className="flex items-center justify-between pt-3 border-t border-[#f0ebe4]">
                  <span className="text-[#999] text-xs">Avg 1BR: {c.cost}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8A830] group-hover:gap-2 transition-all">Explore <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* Why Mexico */}
    <section className="py-20 lg:py-24 bg-[#FFFFFF]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <h2 className="text-3xl lg:text-4xl text-white mb-14" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="why-mexico-heading">Why Americans Are Choosing Mexico</h2>
        <div className="grid md:grid-cols-3 gap-10">
          {stats.map((s) => (
            <div key={s.value}>
              <p className="text-4xl lg:text-5xl font-light text-[#E8A830] mb-3">{s.value}</p>
              <p className="text-[#999] text-sm">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 lg:py-24 bg-[#FAF7F2]">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
        <h2 className="text-3xl text-[#FFFFFF] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Ready to Start?</h2>
        <p className="text-[#666] mb-8 max-w-lg mx-auto">Get your personalized relocation blueprint in 60 seconds. Built around your situation - covering visa, housing, cost of living, and your 90-day action plan.</p>
        <Link to="/relocation/blueprint" className="inline-flex items-center gap-2 bg-[#E8A830] text-white px-8 py-4 text-sm font-semibold rounded-lg hover:bg-[#a8511f] transition-all" data-testid="cta-bottom">
          Get My Blueprint <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>

    <RelocationAffiliate />
    <Footer />
  </div>
);

export default RelocationHub;
